import React, { useRef, useState } from "react";
import { shallowEqual, useDispatch } from "react-redux";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { useSelector } from "react-redux";
import { IRootState } from "../../../../reducer/CombineReducer";
import { OutputConfigSlice } from "../redux";
import { Button, Input, FormElement } from "../../../Common/UI/Index";
import { InputType } from "../../../Common/UI/Types";
import { NotificationBanner } from "../../../Common/NotificationBanner/NotificationMessage";
import { INotification } from "../../../Notification/Index";
import { getNotificationTextBasedOnConfig } from "../helper";
import { CONFIGSAVE } from "../Constant";
import Add from "~icons/ic/baseline-add-circle";
import "./Style.scss";

interface DetailsTabViewProps {
  /**
   * Flag to render the process button
   */
  isDetailsTab: boolean;
  /**
   * Notification message items that needs that are currently available in redux state
   */
  notificationItems: INotification[];
  /**
   * True when saved button in output config full dialog is clicked
   */
  isSaveButtonClick: boolean;
}

/**
 * Responsible for rendering the details tab UI
 * @returns Details tab view component
 */
export const DetailsTabView: React.FC<DetailsTabViewProps> = (props: DetailsTabViewProps): JSX.Element => {
  const { isDetailsTab, notificationItems, isSaveButtonClick } = props;
  const { t: translate } = useTranslation();
  const [file, setFile] = useState<Blob>(new Blob());
  const [configName, setConfigName] = useState("");
  const inputRef = useRef<any>(null);
  const [
    extractState,
    outputConfig
  ] = useSelector((state: IRootState) => [
    state.extractState,
    state.outputConfig
  ], shallowEqual);
  const selectedExtract = extractState.selectedExtract;
  const extractGuid = selectedExtract?.extractGuid as string;
  const dispatch = useDispatch();
  const uploadedFileName = inputRef?.current?.files?.[0]?.name?.split(".")?.[0];
  const { name, fileName } = outputConfig.selectedOutputConfig;
  const notificationMessage = getNotificationTextBasedOnConfig(notificationItems, CONFIGSAVE);

  React.useEffect(() => {
    dispatch(OutputConfigSlice.actions.setEditConfig({
      configName: configName,
      fileName: uploadedFileName || fileName
    }));
  }, [configName, uploadedFileName]);

  /**
   * Request formation to save config
   */
  const requestFormationForSaveOutputConfig = (): void => {
    const data = new FormData();
    data.append("fileData", file);
    data.append("artefactGuid", extractGuid);
    data.append("outputConfigName", configName);
    dispatch(OutputConfigSlice.actions.setFormData(data));
  };

  React.useEffect(() => {
    if (file.size)
      requestFormationForSaveOutputConfig();
  }, [file, configName]);

  /**
   * To set the file data
   */
  const setFileData = (): void => {
    setFile(inputRef.current.files[0]);
  };

  /**
   * To update the config name
   * @param e event attributes
   */
  const handleOnChange = (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setConfigName(e.target["value"]);
  };


  /**
   * Method for opening the dialog while clicking the browse button
   */
  const openDialog = (): void => {
    const uploadElement = document.getElementById("ebt-outputConfig-details-browse-btn");
    if (uploadElement)
      uploadElement.click();
  };

  /**
   * Responsible for dialog open when clicked
   */
  React.useEffect(() => {
    const uploadButtonElement = document.getElementById("ebt-outputConfig-details-upload-btn");
    if (uploadButtonElement)
      uploadButtonElement.addEventListener("click", openDialog);
    return (): void => window.removeEventListener("click", openDialog);
  }, []);

  return (
    <div
      className="ebt-detailstab"
      id="ebt-outputConfig-details"
    >
      {
        ((notificationMessage === translate("Notification.SaveConfig")) && isSaveButtonClick) &&
        <NotificationBanner message={notificationMessage} />
      }
      <FormElement
        labelText="Output configuration name"
        mandatory={true}
        id="ebt-outputConfig-configuration-name"
      >
      </FormElement>
      <Input
        type={InputType.Text}
        name="name"
        data-testid="details-name"
        required={true}
        onChange={handleOnChange}
        value={configName || name}
        id="ebt-outputConfig-details-name"
      />
      <div className="ebt-detailstab__details">
        <FormElement
          labelText="Schema XSD"
          mandatory={true}
          className="ebt-detailstab__input"
        >
          <div>
            <Input
              type={InputType.Text}
              name="name"
              required={true}
              disabled={true}
              value={fileName || uploadedFileName}
              id="ebt-outputConfig-details-schemaXsd"
            />
          </div>
        </FormElement>
        <div className="ebt-detailstab__browsebutton">
          <input
            id="ebt-outputConfig-details-browse-btn"
            type='file'
            hidden
            ref={inputRef}
            onChange={setFileData}
            accept=".xsd"
          />
          <Button
            id="ebt-outputConfig-details-upload-btn"
            disabled={isDetailsTab}
          >
            <Add title="Add item" />
            {translate("Button.Browse")}
          </Button>
        </div>
      </div>
    </div>
  );
};
